import { StandardUniselection } from '@yes.technology/react-toolkit'
import React, { useCallback, useEffect } from 'react'
import { ActivityModel } from 'types/shared'
import { FilteractionParams } from 'utils/apiTypes'
import useFetchSuggestions from '../../../hooks/useFetchSuggestions'
import { ActivityInputProps } from '../Activity.types'

type SelectedMenuOption = Parameters<
  React.ComponentProps<typeof StandardUniselection>['onSelect']
>['0']

type Option = {
  uuid: string
  des: string
}

export type ActivityStandardUniselectionProps = {
  id: string
  parentPath: string[]
  label: string
  value: ActivityModel['value']
  filteraction?: string
  filteractionParams?: FilteractionParams[]
  onChange: ActivityInputProps['handleChange']
  options?: Option[]
  disabled?: boolean
} & Pick<
  React.ComponentProps<typeof StandardUniselection>,
  'id' | 'label' | 'required' | 'statusClass' | 'placeholder'
>

const ActivityStandardUniselection = ({
  id,
  parentPath,
  label,
  value,
  filteraction,
  filteractionParams,
  options = [],
  onChange,
  required,
  statusClass,
  placeholder,
  disabled
}: ActivityStandardUniselectionProps) => {
  const formattedValue: { id: string; value: string } = {
    id: typeof value === 'object' ? value?.id : '',
    value: typeof value === 'object' ? value?.des : ''
  }

  const selectedOption = typeof value === 'object' ? formattedValue : undefined

  const onSourceValuesChange = useCallback(() => {
    if (formattedValue.id) {
      onChange({ id: '', des: '' })
    }
  }, [formattedValue, onChange])

  const { suggestions, fetchSuggestions, isLoading, hasAllSourceValues } =
    useFetchSuggestions({
      parentPath,
      filteraction,
      filteractionParams,
      onSourceValuesChange
    })

  useEffect(() => {
    if (!filteraction || !hasAllSourceValues) {
      return
    }

    fetchSuggestions('.*')
  }, [fetchSuggestions, filteraction, hasAllSourceValues])

  const finalOptions = options.length ? options : suggestions

  const handleChange = (({ id, value }: { id: string; value: string }) => {
    onChange({
      id: id,
      des: value
    })
    // FIXME we need this because toolkit has 2 types for onSelect
  }) as React.ComponentProps<typeof StandardUniselection>['onSelect']

  const loadingStatusClass = isLoading ? 'processing' : undefined

  return (
    <StandardUniselection
      id={id}
      label={label}
      heightClass='regular'
      options={finalOptions.map((rawOption) => ({
        id: rawOption.uuid,
        value: rawOption.des
      }))}
      selectedOption={selectedOption}
      required={required}
      onSelect={(option) => handleChange(option as SelectedMenuOption)}
      statusClass={loadingStatusClass || statusClass}
      placeholder={placeholder}
      disabled={disabled || !hasAllSourceValues}
    />
  )
}

export default ActivityStandardUniselection
